import { useEffect, useState } from "react";
import Nav from "../components/Nav";
import { api, fmt } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash, TrendUp, TrendDown } from "@phosphor-icons/react";

export default function Portfolio() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [symbol, setSymbol] = useState("");
  const [qty, setQty] = useState("");
  const [buy, setBuy] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/portfolio"); setData(r.data); }
    catch (e) { if (e.response?.status === 401) nav("/", { replace: true }); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!symbol || !qty || !buy) return;
    setBusy(true);
    try {
      await api.post("/portfolio", { symbol: symbol.toUpperCase(), quantity: parseFloat(qty), buy_price: parseFloat(buy) });
      toast.success("Holding added");
      setSymbol(""); setQty(""); setBuy("");
      load();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(e.response.data.detail, {
          action: { label: "Upgrade", onClick: () => nav("/pricing") },
        });
      } else {
        toast.error("Failed to add");
      }
    }
    finally { setBusy(false); }
  };

  const remove = async (id) => { await api.delete(`/portfolio/${id}`); load(); };

  const s = data?.summary;
  const plUp = (s?.total_pl ?? 0) >= 0;

  return (
    <div className="min-h-screen bg-paper">
      <Nav />
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-eyebrow">Portfolio</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-1">Track your P&amp;L.</h1>
          </div>
        </div>

        {/* Summary strip */}
        <div className="grid md:grid-cols-4 gap-0 border border-black/10">
          <div className="p-6 border-b md:border-b-0 md:border-r border-black/10">
            <div className="label-eyebrow">Total invested</div>
            <div className="font-mono-num text-3xl mt-2">{fmt.money(s?.total_cost ?? 0)}</div>
          </div>
          <div className="p-6 border-b md:border-b-0 md:border-r border-black/10">
            <div className="label-eyebrow">Current value</div>
            <div className="font-mono-num text-3xl mt-2">{fmt.money(s?.total_value ?? 0)}</div>
          </div>
          <div className={`p-6 border-b md:border-b-0 md:border-r border-black/10 ${plUp ? "bg-pos text-paper" : "bg-neg text-paper"}`}>
            <div className="label-eyebrow" style={{ color: "rgba(255,255,255,0.75)" }}>Unrealized P&amp;L</div>
            <div className="font-mono-num text-3xl mt-2 flex items-center gap-2">
              {plUp ? <TrendUp weight="bold" size={22} /> : <TrendDown weight="bold" size={22} />}
              {fmt.money(s?.total_pl ?? 0)}
            </div>
          </div>
          <div className="p-6">
            <div className="label-eyebrow">Portfolio profit margin</div>
            <div className={`font-mono-num text-3xl mt-2 ${plUp ? "text-pos" : "text-neg"}`}>{fmt.pct(s?.total_pl_pct ?? 0)}</div>
          </div>
        </div>

        {/* Add form */}
        <div className="mt-10 border border-black/10">
          <div className="p-4 border-b border-black/10">
            <div className="label-eyebrow">Add holding</div>
            <h2 className="font-display text-xl mt-1">New position</h2>
          </div>
          <form onSubmit={add} className="p-4 grid md:grid-cols-4 gap-3">
            <input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="Symbol (e.g. AAPL)" className="px-3 py-3 border border-black/15 bg-paper2 focus:border-ikb outline-none text-sm font-mono-num" required data-testid="portfolio-symbol" />
            <input value={qty} onChange={(e) => setQty(e.target.value)} type="number" step="any" placeholder="Quantity" className="px-3 py-3 border border-black/15 bg-paper2 focus:border-ikb outline-none text-sm font-mono-num" required data-testid="portfolio-qty" />
            <input value={buy} onChange={(e) => setBuy(e.target.value)} type="number" step="any" placeholder="Buy price ($)" className="px-3 py-3 border border-black/15 bg-paper2 focus:border-ikb outline-none text-sm font-mono-num" required data-testid="portfolio-price" />
            <button type="submit" disabled={busy} className="btn-primary justify-center" data-testid="portfolio-add">
              <Plus size={14} /> {busy ? "Adding…" : "Add holding"}
            </button>
          </form>
        </div>

        {/* Holdings table */}
        <div className="mt-10 border border-black/10 overflow-hidden">
          <div className="p-4 border-b border-black/10">
            <div className="label-eyebrow">Holdings</div>
            <h2 className="font-display text-xl mt-1">{data?.holdings?.length ?? 0} positions</h2>
          </div>
          {data?.holdings?.length ? (
            <table className="w-full text-sm">
              <thead className="bg-paper2 text-xs uppercase tracking-wider text-ink2">
                <tr>
                  <th className="text-left px-4 py-3">Symbol</th>
                  <th className="text-right px-4 py-3">Qty</th>
                  <th className="text-right px-4 py-3">Buy</th>
                  <th className="text-right px-4 py-3">Current</th>
                  <th className="text-right px-4 py-3">Value</th>
                  <th className="text-right px-4 py-3">P&amp;L</th>
                  <th className="text-right px-4 py-3">Margin %</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="font-mono-num">
                {data.holdings.map((h) => {
                  const up = h.pl >= 0;
                  return (
                    <tr key={h.id} className="border-t border-black/10 hover:bg-paper2" data-testid={`holding-row-${h.symbol}`}>
                      <td className="px-4 py-3">
                        <Link to={`/stock/${h.symbol}`} className="font-bold hover:text-ikb">{h.symbol}</Link>
                        <div className="text-xs text-ink2 font-sans">{h.name}</div>
                      </td>
                      <td className="text-right px-4 py-3">{h.quantity}</td>
                      <td className="text-right px-4 py-3">{fmt.money(h.buy_price)}</td>
                      <td className="text-right px-4 py-3">{fmt.money(h.current_price)}</td>
                      <td className="text-right px-4 py-3">{fmt.money(h.value)}</td>
                      <td className={`text-right px-4 py-3 ${up ? "text-pos" : "text-neg"}`}>{fmt.money(h.pl)}</td>
                      <td className={`text-right px-4 py-3 ${up ? "text-pos" : "text-neg"}`}>{fmt.pct(h.pl_pct)}</td>
                      <td className="text-right px-4 py-3">
                        <button onClick={() => remove(h.id)} className="text-ink2 hover:text-neg" data-testid={`holding-remove-${h.symbol}`} aria-label="Remove">
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="p-10 text-center text-ink2 text-sm">No holdings yet. Add your first position above.</div>
          )}
        </div>
      </main>
    </div>
  );
}
